import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortQueryKeys } from './queryKeys';
import { objectResponse } from './schemas';

/** Matches the backend's `ArtifactKind` naming for cohort / analytical dataset. */
export type AssetStalenessKind = 'cohort' | 'analytical_dataset';

const staleAnalyticalVariableRefSchema = z.object({
  analyticalVariableId: z.string(),
  name: z.string(),
});

const staleCohortRefSchema = z.object({
  cohortProfileId: z.string(),
  name: z.string(),
});

const assetStalenessSchema = z
  .object({
    isStale: z.boolean(),
    reasons: z.array(z.string()).default([]),
    staleAnalyticalVariables: z.array(staleAnalyticalVariableRefSchema).default([]),
    staleCohorts: z.array(staleCohortRefSchema).default([]),
  })
  .passthrough();

export type AssetStaleness = z.infer<typeof assetStalenessSchema>;

interface UseAssetStalenessQueryParams {
  kind: AssetStalenessKind;
  /** The asset's *version* id — cohort_version_id or analyticalDatasetVersionId. */
  id: string;
  /** Force-disable the query (default: enabled when both kind and id are set). */
  enabled?: boolean;
}

/**
 * Generic read for `GET /asset-staleness?kind=&id=` — replaces the
 * `staleness` field that used to be attached to the cohort/AD version GET
 * responses. `data` is `null` when `asset_edit_while_in_use` is
 * off (no signal), same semantics as the old inline field.
 */
export const useAssetStalenessQuery = ({ kind, id, enabled }: UseAssetStalenessQueryParams) => {
  return useQuery<AssetStaleness | null>({
    queryKey: cohortQueryKeys.assetStaleness(kind, id),
    queryFn: async () => {
      const raw = await authFetch<unknown>(
        `/asset-staleness?kind=${kind}&id=${encodeURIComponent(id)}`
      );
      const envelope = parseOrFallback(
        objectResponse(assetStalenessSchema.nullable()),
        raw,
        'useAssetStalenessQuery'
      );
      return envelope.data;
    },
    enabled: enabled !== false && !!kind && !!id,
  });
};
